/**
 * The numbers a Channel filter can bound (CTB-02): how big and how active a
 * Channel is. Every value comes from what the Channels tab already loads, the
 * channel list, its stats and the in-Scope counts; nothing is fetched for it.
 * A Channel with no value for a number is not measured, and fails any bound
 * on it.
 */
import type { ChannelMomentKey } from "@/lib/channels/channel-moments"
import { formatCount } from "@/lib/format-count"
import type { Channel, ChannelStats } from "@/types"

export type MetricKey =
  | "subscribers"
  | "reach"
  | "activity_rate"
  | "total_posts"
  | "posts_in_scope"
  | "photos"
  | "videos"
  | "files"
  | "links"

export type MetricInputs = {
  channelStats: Record<string, ChannelStats>
  postsInScopeCounts: Record<string, number>
  /**
   * The Scope's server minute a Live Time bound is judged against (TB-03), so
   * the filter moves on the minute tick rather than the browser's clock.
   */
  now: number
}

type Metric = {
  key: MetricKey
  label: string
  unit?: string
  value: (channel: Channel, inputs: MetricInputs) => number | null
}

/** In the Filters dropdown's order. The key is the URL form's word. */
export const METRICS: Metric[] = [
  {
    key: "subscribers",
    label: "Subscribers",
    value: (c) => c.subscribers ?? null,
  },
  {
    key: "reach",
    label: "Reach",
    value: (c, i) => i.channelStats[c.name]?.reach ?? null,
  },
  {
    key: "activity_rate",
    label: "Activity rate",
    unit: "posts per hour",
    value: (c, i) => i.channelStats[c.name]?.velocity ?? null,
  },
  {
    key: "total_posts",
    label: "Total posts",
    value: (c, i) => i.channelStats[c.name]?.count ?? null,
  },
  {
    // Every Channel is measured: one outside the Scope has none in it.
    key: "posts_in_scope",
    label: "Posts in scope",
    value: (c, i) => i.postsInScopeCounts[c.name] ?? 0,
  },
  { key: "photos", label: "Photos", value: (c) => c.photos ?? null },
  { key: "videos", label: "Videos", value: (c) => c.videos ?? null },
  { key: "files", label: "Files", value: (c) => c.files ?? null },
  { key: "links", label: "Links", value: (c) => c.links ?? null },
]

const BY_KEY = new Map(METRICS.map((m) => [m.key, m]))

export const metric = (key: MetricKey): Metric => BY_KEY.get(key) as Metric

export const isMetricKey = (word: string): word is MetricKey =>
  BY_KEY.has(word as MetricKey)

export const metricValue = (
  key: MetricKey,
  channel: Channel,
  inputs: MetricInputs,
): number | null => metric(key).value(channel, inputs)

/** The measured values of a metric across `channels`, ascending. */
export const metricValues = (
  key: MetricKey,
  channels: Channel[],
  inputs: MetricInputs,
): number[] =>
  channels
    .map((c) => metricValue(key, c, inputs))
    .filter((v): v is number => v !== null)
    .sort((a, b) => a - b)

/**
 * A bound on one metric, either end open, or `none`: the Channels with no
 * value for it.
 */
export type MetricBound = { min?: number; max?: number; none?: true }

/** Which of the four shapes a bound has. */
export type BoundKind = "gte" | "lte" | "between" | "none"

export const boundKind = (b?: MetricBound): BoundKind =>
  b?.none
    ? "none"
    : b?.min !== undefined && b.max !== undefined
      ? "between"
      : b?.max !== undefined
        ? "lte"
        : "gte"

/**
 * The bound a bound editor's fields spell, or null while it is incomplete or
 * backwards. `min` refuses a value under it, for a measure that has a floor
 * the server enforces (a View count is never negative).
 */
export function parseBound(
  op: BoundKind,
  a: string,
  b: string,
  { min }: { min?: number } = {},
): MetricBound | null {
  if (op === "none") return { none: true }
  const num = (text: string) => {
    const n = Number(text)
    return text.trim() === "" ||
      !Number.isFinite(n) ||
      (min !== undefined && n < min)
      ? undefined
      : n
  }
  const [x, y] = [num(a), num(b)]
  if (x === undefined) return null
  if (op === "gte") return { min: x }
  if (op === "lte") return { max: x }
  return y === undefined || y < x ? null : { min: x, max: y }
}

/**
 * A bound on a whole number (a Channel posts count, PTI-02) rounded inward,
 * low end up and high end down, so "<= 29.5" keeps exactly what "<= 29"
 * keeps; `null` when no whole number is left between its ends.
 */
export function wholeBound(bound: MetricBound): MetricBound | null {
  const min = bound.min === undefined ? undefined : Math.ceil(bound.min)
  const max = bound.max === undefined ? undefined : Math.floor(bound.max)
  if (min !== undefined && max !== undefined && min > max) return null
  return {
    ...(min === undefined ? {} : { min }),
    ...(max === undefined ? {} : { max }),
  }
}

/** What a bound editor needs of the Account's Channels. */
export type MetricData = {
  /** A metric's measured values across the Account's Channels, ascending. */
  values: (key: MetricKey) => number[]
  /** Every Channel, measured or not. */
  total: number
  /** A moment's values across the Account's Channels, ascending (TB-03). */
  moments: (key: ChannelMomentKey) => number[]
}

/** A missing value fails every bound and passes only `none`. */
export function inBound(value: number | null, bound: MetricBound): boolean {
  if (bound.none) return value === null
  return (
    value !== null &&
    (bound.min === undefined || value >= bound.min) &&
    (bound.max === undefined || value <= bound.max)
  )
}

/** The bound alone, "≥ 1.2K", "500–20K" or "no value". */
export function boundText(b: MetricBound): string {
  const [min, max] = [formatCount(b.min ?? 0), formatCount(b.max ?? 0)]
  return {
    none: "no value",
    between: `${min}–${max}`,
    gte: `≥ ${min}`,
    lte: `≤ ${max}`,
  }[boundKind(b)]
}
